/**
 * The Sahas Forge Agent's identities: typed wrappers over `/v1/ingest/agents` plus their React Query hooks (backend
 * `platform-core/internal/ingestagent/identities.go`, contract `api/openapi.yaml` IngestAgent…). Org admins only.
 *
 * What the server decides, and the UI only shows:
 *   - an identity file (`.sfid`) exists only in the answer to an issue or a re-issue: it is never stored and cannot be
 *     downloaded again. It carries a one-time setup key, valid for 7 days by default;
 *   - a re-issue ends the PC's activation and makes the old file useless; a revoke ends the identity for good;
 *   - with no agent keys configured on the platform, issuing answers 503;
 *   - (agent 0.3.0, migration 0218) the settings managed for a PC from the cloud replace the ones before, bump its
 *     `managed_seq`, and reach the agent on its next sync; a revoked identity answers 409. The call replaces ALL of
 *     them: a body that leaves a key out takes it away (see lib/plant-agent-settings.ts, which starts from what it loaded);
 *   - (agent 0.4.0, migration 0231) an org admin can ask a PC to inventory itself (`explore`) or to browse an OPC UA
 *     server on it (`opcua_browse`), both signed requests that reach the agent on its sync. An opt-in needs the PC's
 *     managed `explore_consent` (else 409 `consent_required`), and someone at the PC must have confirmed it in the
 *     agent's window (else 409 `consent_not_confirmed`; Arjun's decision on #1419, 2026-10-14: `consent` reads where it
 *     stands, with its history). The explore documents go up as a log bundle with reason `explore` that platform
 *     admins (Sahas staff) and, since 2026-10-14, the org's own admins read (`GET /v1/ingest/agents/{id}/explore-bundles`,
 *     backend explore_bundles.go; no screen yet); an org admin can also upload the sealed file (`.sfbundle`) a PC wrote
 *     to a pendrive.
 *
 * Agent releases and key updates (`/v1/platform/agent-releases`, `/v1/platform/agent-trust`, backend releases.go and
 * trust.go) are for platform admins only, and not org-scoped: one agent build serves every org. Publishing is done with
 * divineiotctl, as signing is offline; the UI lists, marks urgent and withdraws.
 *
 * Agent log bundles (`/v1/platform/agent-logs`, backend logs.go and diagnostics.go) are for platform admins only too,
 * and cross-org: the list (by org or agent), a bundle's diagnostics.json read without a download, the download, and
 * "ask for logs now". An agent's online history is org-scoped (`/v1/ingest/agents/{id}/presence`, org admin).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

export type IngestAgentStatus = "issued" | "active" | "revoked";

/** How the agent sends: probe (everything, the newest of each kind first) or operational (only kinds with a verified reader). */
export type IngestSendMode = "probe" | "operational";
export type IngestUpdatePolicy = "auto" | "notify" | "off";
export type IngestPath = "auto" | "direct-only" | "relay-only";

/**
 * Agent 0.4.0 (migration 0231): what an admin ASKS an explore request may do on the PC beyond step 0, which always
 * runs. It applies only once someone at the PC has confirmed it in the agent's window (IngestAgentConsent). A request
 * for anything not asked is refused (409 `consent_required`), one for what is asked but not confirmed at the PC too (409
 * `consent_not_confirmed`), and the agent checks again. Unset (no `explore_consent`): nothing beyond step 0, and
 * opcua_browse is refused. The same shape carries what the PC confirmed, and what applies.
 */
export interface IngestExploreConsent {
  /** Names, CPU and memory of running processes (never command lines or users). */
  processes?: boolean;
  /** The PC's ARP cache. */
  arp?: boolean;
  /** The logged-on user's name. */
  user?: boolean;
  /** Deep scans of whole drives, and so of any folder. */
  whole_drives?: boolean;
  /** Reading an OPC UA server on the PC: explore's `opcua_test_url`, and opcua_browse. */
  opcua_test?: boolean;
  /** Logging in to the PC's own SQL Server. */
  sql_test?: boolean;
  /** Testing the link to Sahas Forge, and to a gateway. */
  net_test?: boolean;
  /** Folders a deep scan may cover, and any folder below them: at most 8, absolute drive paths. A drive's root (D:\) needs `whole_drives`. */
  deep_roots?: string[];
  /** Read-only, the cloud's: the admin who last changed what is asked (an email, else a user id, else "api")... */
  given_by?: string;
  /** ...and when. A value sent for either is ignored; saving the same ask again keeps them. */
  given_at?: string;
}

/** Agent 0.4.0: one change of a PC's explore consent (IngestAgentConsentEvent). */
export interface IngestAgentConsentEvent {
  id: string;
  /** asked, narrowed: an admin's save (asked for more, or only for less); confirmed, declined, withdrawn: at the PC. */
  event: "asked" | "narrowed" | "confirmed" | "declined" | "withdrawn";
  /** The admin (an email, else a user id, else "api"), or "at the PC: <Windows account>". */
  by: string;
  source: "admin" | "pc";
  /** When: the cloud's clock for an admin, the PC's clock for the PC. */
  at: string;
  /** When the cloud wrote it down (a PC that was offline reports later). */
  recorded_at: string;
  /** What it is about: the ask as it is now (asked, narrowed), or what the PC confirmed, declined or withdrew. */
  consent: IngestExploreConsent;
  /** The managed settings' seq. */
  seq: number;
}

/**
 * Agent 0.4.0 (Arjun's decision on #1419, 2026-10-14): a PC's explore consent as it stands (IngestAgentConsent): what an
 * admin asks, what someone at the PC confirmed (their Windows account, and when by the PC's clock), what applies (what
 * both allow), what waits for the PC, its last answer, and the latest changes, newest first.
 */
export interface IngestAgentConsent {
  agent_id: string;
  asked: IngestExploreConsent | null;
  confirmed: IngestExploreConsent | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  effective: IngestExploreConsent | null;
  /** Asked but not confirmed at the PC, as a refusal names them: "processes", …, "deep root D:\X". */
  waiting: string[];
  last_answer: IngestAgentConsentEvent | null;
  history: IngestAgentConsentEvent[];
}

/**
 * The settings managed for one PC from the cloud (IngestManagedSettings). Only the keys set are managed; the rest stay
 * the PC's own. `{}` manages nothing.
 */
export interface IngestManagedSettings {
  /** remote (the default): locked on the PC. local: applied when changed, then the PC may change them. */
  control?: "remote" | "local";
  mode?: IngestSendMode;
  /** Probe: the newest files of each kind sent first, 1–20. */
  samples_per_kind?: number;
  /** Files may be dropped on the agent's window. */
  drop?: boolean;
  /** A share of the PC's measured upload speed, 5–100 (%). */
  bandwidth?: { catch_up_pct?: number; after_pct?: number };
  updates?: {
    policy?: IngestUpdatePolicy;
    /** A version (X.Y.Z) to hold the PC at, older or newer. */
    pin?: string;
    /** Every update offered to this PC is urgent (forced). */
    urgent?: boolean;
  };
  path?: IngestPath;
  /** Minutes between passes, 1–1440. */
  interval_minutes?: number;
  /** Files per pass, 1–1000. */
  max_per_pass?: number;
  /** Agent 0.3.2: the folders to watch (replacing the PC's own), the patterns to send, and remote browse. */
  folders?: { path: string; recursive?: boolean; existing?: "all" | "new" }[];
  patterns?: string[];
  remote_browse?: boolean;
  /** Agent 0.4.0: what explore and opcua_browse may do on the PC beyond step 0. Unset: nothing beyond step 0. */
  explore_consent?: IngestExploreConsent;
  /**
   * Agent 0.4.0: whether the agent window's "Save to a pendrive" works on the PC. Unset (not managed): on in probe
   * mode, off in operational mode. Only the cloud turns it on in operational mode, so it applies with either control.
   */
  pendrive_save?: boolean;
}

/** Agent 0.3.2 (migration 0220): online now, or offline since when; worked out when the API answers. */
export interface IngestAgentPresence {
  state: "online" | "offline" | "unknown";
  /** Online since it came back, or offline since it was last heard from. */
  since: string | null;
  /** "direct", or "gateway <thing id>". */
  via: string | null;
  /** The gateway `via` names, when it is a gateway of the org (else null; absent from an older API). */
  via_gateway_id?: string | null;
  /** That gateway's name, e.g. "Gateway 7E1A90A1". */
  via_gateway_name?: string | null;
  /** Paused by someone at the PC. */
  agent_state: "running" | "paused" | null;
  /** The agent holds a long poll (0.3.0+): known within seconds; else within a pass. */
  live: boolean;
}

/** The agent's last update, as it reported it: updated to, rolled back from, or failed to install `update_version`. */
export type IngestUpdateState = "updated" | "rolled_back" | "failed";

export interface IngestAgent {
  id: string;
  /** The identity's printed id, e.g. SFA-F5TD-209T. */
  identity_id: string;
  name: string;
  status: IngestAgentStatus;
  generation: number;
  relay_policy: { mode: "any_org_gateway" | "listed"; gateway_ids?: string[] };
  issued_at: string;
  setup_expires_at: string;
  /** An issued identity whose setup key can no longer activate a PC. */
  setup_expired: boolean;
  setup_used_at: string | null;
  activated_at: string | null;
  revoked_at: string | null;
  last_seen_at: string | null;
  last_relayed_by: string | null;
  last_relayed_at: string | null;
  /** The name of the gateway `last_relayed_by` names, when it is a gateway of the org (absent from an older API). */
  last_relayed_by_name?: string | null;
  host_name: string | null;
  agent_version: string | null;
  pc_fingerprint: string | null;
  created_at: string;
  /** `{}` when nothing is managed. */
  managed_settings: IngestManagedSettings;
  /** Bumped by every change; 0: never managed. */
  managed_seq: number;
  /** Who changed them last: an email, or "api". */
  managed_by: string | null;
  managed_at: string | null;
  update_state: IngestUpdateState | null;
  update_version: string | null;
  update_error: string | null;
  update_at: string | null;
  /** Agent 0.3.2; absent from an older API. */
  presence?: IngestAgentPresence;
}

/** The answer to an issue or a re-issue: the only time the file exists. Save `identity_file` byte for byte. */
export interface IngestAgentIssued {
  agent: IngestAgent;
  file_name: string;
  identity_file: string;
}

/** A published agent build (IngestAgentRelease). */
export interface AgentRelease {
  id: string;
  product: string;
  /** The installer kind it is for, e.g. windows-x64-nsis. */
  target: string;
  channel: string;
  version: string;
  sha256: string;
  /** Bytes. */
  size: number;
  /** The release key that signed it. */
  kid: string;
  manifest: Record<string, unknown>;
  created_at: string;
  /** Every agent offered it installs it at once. */
  urgent: boolean;
  /** Withdrawn: never offered or served again. */
  withdrawn_at: string | null;
}

/** A key update (IngestSignedTrust): a trust bundle the Sahas root signed offline. */
export interface AgentSignedTrust {
  bundle: { seq: number; issued_at: string; note?: string } & Record<string, unknown>;
  signature: string;
}

/**
 * Why an agent sent a log bundle: once a day, after a problem (an outage or a failed update), asked for, by hand; and
 * (agent 0.4.0) `explore`: the documents of an explore run, sent online or uploaded from a pendrive.
 */
export type AgentLogReason = "daily" | "problem" | "request" | "manual" | "explore";

/** A log bundle an agent sent (IngestAgentLogBundle). */
export interface AgentLogBundle {
  id: string;
  /** The identity's row id (IngestAgent.id). */
  agent_id: string;
  /** The identity's printed id, e.g. SFA-F5TD-209T. */
  identity_id: string;
  agent_name: string;
  org_id: string;
  /** The plant's name, joined by the API from the org; null when the org row is gone. Absent from an older API. */
  org_name?: string | null;
  reason: AgentLogReason;
  agent_version: string | null;
  /** How it reached the cloud: "direct", or "gateway <thing id>". */
  via: string | null;
  /** Bytes. */
  size: number;
  sha256: string;
  created_at: string;
}

/** A certificate as the agent read it (agent-core engine/diag.rs CertInfo). */
export interface AgentCertInfo {
  subject: string;
  issuer: string;
  not_before?: string | null;
  not_after?: string | null;
}

/** A connection problem, explained by the agent (diag.rs Problem). */
export interface AgentProblem {
  /** dns, timeout, refused, reset, intercepted, clock, proxy_auth, web_filter, not_sahas, tls, unreachable. */
  code: string;
  title: string;
  advice: string;
  detail: string;
  certificate?: AgentCertInfo | null;
}

/** One step of the agent's connection check (engine/check.rs Check). */
export interface AgentConnectionCheck {
  label: string;
  result: "ok" | "fail" | "skip";
  detail: string;
}

/**
 * The agent's diagnostics.json, as far as the page reads it (diag.rs `report`). Every field may be missing: the
 * report is the agent's, and differs between versions.
 */
export interface AgentDiagnosticsReport {
  made_at?: string;
  reason?: string;
  api_base?: string;
  agent?: { version?: string; os?: string; arch?: string; windows?: string | null; pc_name?: string };
  health?: {
    online?: boolean | null;
    /** Unix seconds. */
    since?: number | null;
    via?: string | null;
    problem?: AgentProblem | null;
    /** [seconds offline, ended (Unix seconds)]. */
    last_outage?: [number, number] | null;
    /** An outage ended and its logs had not been sent: this bundle is them. */
    outage_unsent?: boolean;
    direct_problem?: AgentProblem | null;
  };
  connection_check?: {
    adapter?: string;
    source_ip?: string;
    direct?: boolean;
    checks?: AgentConnectionCheck[];
    problem?: AgentProblem | null;
    /** The agent's note about the Windows proxy, when the check failed. */
    proxy?: string | null;
    certificate?: AgentCertInfo | null;
  };
  /** How Windows is set to reach the web, when it uses a proxy; null when it does not. */
  proxy?: string | null;
  /** last_outcome: "updated:0.3.2", "failed:0.3.2:why", "rolled_back:0.3.2:why". */
  update?: { offer?: string | null; ready?: string | null; last_outcome?: string | null } | null;
  identity?: { identity_id?: string; agent_name?: string; org_name?: string };
}

/** A bundle's diagnostics.json and its files (names and sizes, never contents). */
export interface AgentLogDiagnostics {
  id: string;
  diagnostics: AgentDiagnosticsReport;
  files: { name: string; size: number }[];
  file_count: number;
}

/** One line of an agent's online history (IngestAgentPresenceEvent). */
export interface AgentPresenceEvent {
  event: "online" | "offline" | "offline_long" | "paused" | "resumed";
  at: string;
  via: string | null;
  detail: string | null;
}

export interface AgentPresenceHistory {
  agent_id: string;
  presence: IngestAgentPresence;
  events: AgentPresenceEvent[];
}

// ── Requests to a PC: browse and fetch (agent 0.3.2), explore and opcua_browse (agent 0.4.0) ──────────────────

/** What an explore request asks beyond step 0, which always runs. Every field is optional in a request. */
export interface IngestExploreParams {
  processes?: boolean;
  arp?: boolean;
  user?: boolean;
  /** Folders to scan in depth, at most 8: absolute drive paths (D:\X\Y), no UNC path, no `..` step. A drive's root needs `whole_drives`. */
  deep_roots?: string[];
  whole_drives?: boolean;
  /** Read this OPC UA server on the PC: opc.tcp:// to localhost, 127.0.0.1 or [::1] only. Empty: no test. */
  opcua_test_url?: string;
  sql_test?: boolean;
  /** With `sql_test`: the instance on this PC, as .\NAME, (local)\NAME or localhost\NAME. Empty: the agent decides. */
  sql_instance?: string;
  net_test?: boolean;
  /** With `net_test`: the gateway's IPv4 address. Empty: none. */
  gateway?: string;
  /** The whole run ends by then: 1–30, default 10. */
  max_minutes?: number;
}

/** What an opcua_browse request asks: browse and read one OPC UA server on the PC itself (anonymous, never a write). */
export interface IngestOpcuaBrowseParams {
  /** opc.tcp:// to localhost, 127.0.0.1 or [::1] only (a port and a path may follow). */
  url: string;
  /** Levels below the root: 1–10, default 6. */
  max_depth?: number;
  /** Nodes listed: 1–20000, default 3000. */
  max_nodes?: number;
  /** Values read: 0–20000, default 2000 (0: none). */
  max_read?: number;
  /** Milliseconds between two calls to the server: 5–1000, default 20. */
  gap_ms?: number;
  /** Seconds the whole browse may take: 10–600, default 120. */
  deadline_s?: number;
}

export type IngestRequestKind = "browse" | "fetch" | "explore" | "opcua_browse";

/** An explore or opcua_browse the PC has taken stays `pending` while its run goes on. */
export type IngestRequestStatus = "pending" | "done" | "refused" | "failed" | "expired";

/** The agent's answer to a request (IngestAgentRequestResult). */
export interface IngestAgentRequestResult {
  status: "done" | "refused" | "failed";
  reason?: string;
  browse?: {
    path?: string;
    truncated?: boolean;
    entries?: { name: string; path?: string; dir: boolean; size?: number; modified?: string; watched?: boolean; denied?: boolean }[];
  };
  fetch?: { queued?: number; skipped?: { path?: string; why?: string }[] };
  /** Agent 0.4.0: the agent's summary of an explore run, as it sent it (the documents are in the log bundle with reason `explore`). */
  explore?: Record<string, unknown>;
  /** Agent 0.4.0: the agent's answer to an opcua_browse, what it found, as it sent it. */
  opcua_browse?: Record<string, unknown>;
}

/** An org admin's request to a PC (IngestAgentRequest). */
export interface IngestAgentRequest {
  id: string;
  agent_id: string;
  kind: IngestRequestKind;
  status: IngestRequestStatus;
  /** browse: the folder (empty: the drives and watched folders). */
  path?: string;
  /** fetch: the files or folders. */
  paths?: string[];
  recursive?: boolean;
  /** explore: what was asked, as signed: every field present, the defaults filled in. */
  explore?: Required<IngestExploreParams>;
  /** opcua_browse: what was asked, as signed: every field present, the defaults filled in. */
  opcua_browse?: Required<IngestOpcuaBrowseParams>;
  created_by: string;
  created_at: string;
  expires_at: string;
  /** When it last went out on the agent's sync. */
  delivered_at: string | null;
  result_at: string | null;
  result: IngestAgentRequestResult | null;
}

/** The answer to an uploaded pendrive bundle: the log bundle its zip is kept as, and whether the PC's device key signed it. */
export interface IngestBundleUpload {
  /** The log bundle (GET /v1/platform/agent-logs/{id}), reason `explore`. */
  id: string;
  /** The zip's size, in bytes. */
  size: number;
  sha256: string;
  identity_id: string;
  kind: "explore";
  computer_name: string;
  /** When the agent wrote the bundle (RFC 3339, by the PC's clock). */
  created_utc: string;
  /** Signed by the device key bound to this identity at activation. */
  device_verified: boolean;
  /** Why it is not device-verified: unsigned; the identity is not activated yet; or it names a key the identity no longer has. */
  device_check?: "unsigned" | "not_activated" | "other_device_key";
  /** The agent version that wrote the bundle (from its sealed manifest). */
  agent_version?: string;
  /** Seconds between the bundle's writing and its upload. */
  age_s?: number;
  /** Written more than 30 days before the upload, or more than a day after it (a fast PC clock). */
  stale?: boolean;
  /** The same zip from this agent was taken before: `id` is that bundle's. */
  duplicate?: boolean;
}

/** The longest `wait` the API takes for an answer (seconds). */
export const REQUEST_WAIT_MAX_S = 25;

/** `?wait=N` for a request call, N a whole number of seconds from 0 to 25; nothing when no wait is asked. */
function waitQuery(wait?: number): string {
  if (wait === undefined || !Number.isFinite(wait)) return "";
  return `?wait=${Math.min(REQUEST_WAIT_MAX_S, Math.max(0, Math.floor(wait)))}`;
}

const enc = encodeURIComponent;

/** The explore consent's changes the settings dialog shows (agent 0.4.0). */
export const CONSENT_HISTORY_SHOWN = 8;

export const ingestAgents = {
  list: () => api.get<{ items: IngestAgent[] }>("/v1/ingest/agents"),
  issue: (body: { name: string; setup_valid_days?: number }) => api.post<IngestAgentIssued>("/v1/ingest/agents", body),
  reissue: (id: string) => api.post<IngestAgentIssued>(`/v1/ingest/agents/${enc(id)}/reissue`, {}),
  revoke: (id: string) => api.delete<void>(`/v1/ingest/agents/${enc(id)}`),
  /** Replaces what is managed for the PC (`{}` manages nothing) and answers the identity, its seq bumped. */
  putSettings: (id: string, settings: IngestManagedSettings) =>
    api.put<IngestAgent>(`/v1/ingest/agents/${enc(id)}/settings`, { settings }),
  /**
   * Agent 0.4.0, org admin: asks the PC to make a read-only inventory of itself. Step 0 always runs; each opt-in needs
   * the PC's managed `explore_consent` (else 409 consent_required). A run takes minutes, so the usual answer is 202
   * with the request `pending`; with `wait` (0–25 s) the call waits at most that long. Follow it with `getRequest`.
   */
  explore: (id: string, params: IngestExploreParams = {}, wait?: number) =>
    api.post<IngestAgentRequest>(`/v1/ingest/agents/${enc(id)}/explore${waitQuery(wait)}`, params),
  /** Agent 0.4.0, org admin: asks the PC to browse and read one OPC UA server on it (needs `explore_consent.opcua_test`). */
  opcuaBrowse: (id: string, params: IngestOpcuaBrowseParams, wait?: number) =>
    api.post<IngestAgentRequest>(`/v1/ingest/agents/${enc(id)}/opcua-browse${waitQuery(wait)}`, params),
  /** One request to the PC, with its result; with `wait` (0–25 s) the call waits at most that long for the answer. */
  getRequest: (id: string, requestId: string, wait?: number) =>
    api.get<IngestAgentRequest>(`/v1/ingest/agents/${enc(id)}/requests/${enc(requestId)}${waitQuery(wait)}`),
  /**
   * Agent 0.4.0, org admin: uploads the sealed file (`.sfbundle`) a PC wrote to a pendrive, as it is (the body is the
   * file: at most 8 MiB + 64 KiB). Only Sahas Forge opens it; the zip is kept as a log bundle with reason `explore`.
   * 201: kept. 200 with `duplicate`: the same zip from this agent was taken before.
   */
  uploadBundle: (id: string, file: Blob) =>
    api.postRaw<IngestBundleUpload>(`/v1/ingest/agents/${enc(id)}/bundles`, file),
  /** Agent 0.4.0, org admin: the explore consent as it stands, with the latest `limit` changes (1–100). */
  consent: (id: string, limit = CONSENT_HISTORY_SHOWN) =>
    api.get<IngestAgentConsent>(`/v1/ingest/agents/${enc(id)}/consent?limit=${limit}`),
};

/** A PC's explore consent (org admin). Read again every 15 s: someone at the PC may answer while the dialog is open. */
export function useIngestAgentConsentQuery(agentId: string, enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["ingest-agent-consent", agentId],
    queryFn: () => ingestAgents.consent(agentId),
    enabled: live && enabled,
    staleTime: 5_000,
    refetchInterval: 15_000,
    retry: false,
  });
}

/** Platform admins only. */
export const agentReleases = {
  list: () => api.get<{ items: AgentRelease[] }>("/v1/platform/agent-releases"),
  withdraw: (id: string) => api.post<AgentRelease>(`/v1/platform/agent-releases/${enc(id)}/withdraw`, {}),
  setUrgent: (id: string, urgent: boolean) =>
    api.post<AgentRelease>(`/v1/platform/agent-releases/${enc(id)}/urgent`, { urgent }),
  trust: () => api.get<{ items: AgentSignedTrust[] }>("/v1/platform/agent-trust"),
};

/** The newest log bundles asked for at once (the API's most). */
export const AGENT_LOGS_LIMIT = 200;

/** Platform admins only, but `presence`. */
export const agentLogs = {
  /** The newest bundles first, for one org or every org (null). */
  list: (orgId: string | null) => {
    const q = new URLSearchParams({ limit: String(AGENT_LOGS_LIMIT) });
    if (orgId) q.set("org_id", orgId);
    return api.get<{ items: AgentLogBundle[] }>(`/v1/platform/agent-logs?${q.toString()}`);
  },
  diagnostics: (id: string) => api.get<AgentLogDiagnostics>(`/v1/platform/agent-logs/${enc(id)}/diagnostics`),
  /** The zip as the agent sent it. */
  download: (id: string) => api.getBlob(`/v1/platform/agent-logs/${enc(id)}`),
  /** Asks the PC for its logs: its next sync says so, and it sends a bundle with reason request. */
  request: (agentId: string) =>
    api.post<{ agent_id: string; seq: number; status: string }>("/v1/platform/agent-logs/request", { agent_id: agentId }),
  /** Org admin, in the agent's org: its presence now and its last changes, newest first. */
  presence: (agentId: string) => api.get<AgentPresenceHistory>(`/v1/ingest/agents/${enc(agentId)}/presence?limit=50`),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live fetches require the build flag AND an authenticated, org-resolved session. */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const INGEST_AGENTS_KEY = ["ingest-agents"] as const;

/** The org's agent identities, newest first. Refreshed every minute, so a PC's activation shows up on its own. */
export function useIngestAgentsQuery(enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: INGEST_AGENTS_KEY,
    queryFn: () => ingestAgents.list(),
    enabled: live && enabled,
    // Presence (agent 0.3.2) is live: an agent going offline shows within ~15 s of the cloud seeing it.
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

function useAgentsMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INGEST_AGENTS_KEY });
    },
  });
}

export function useIssueIngestAgent() {
  return useAgentsMutation((body: { name: string; setup_valid_days?: number }) => ingestAgents.issue(body));
}

export function useReissueIngestAgent() {
  return useAgentsMutation((id: string) => ingestAgents.reissue(id));
}

export function useRevokeIngestAgent() {
  return useAgentsMutation((id: string) => ingestAgents.revoke(id));
}

/** Saves a PC's managed settings: the answer replaces its row at once (its seq bumped), then the list is read again. */
export function usePutIngestAgentSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; settings: IngestManagedSettings }) => ingestAgents.putSettings(v.id, v.settings),
    onSuccess: (agent) => {
      qc.setQueryData<{ items: IngestAgent[] }>(INGEST_AGENTS_KEY, (old) =>
        old ? { items: old.items.map((a) => (a.id === agent.id ? agent : a)) } : old,
      );
      void qc.invalidateQueries({ queryKey: INGEST_AGENTS_KEY });
      // A save may ask for more or less of the explore consent: its state and history change with it.
      void qc.invalidateQueries({ queryKey: ["ingest-agent-consent", agent.id] });
    },
  });
}

// ── Agent releases and key updates (platform admins) ─────────────────────────

export const AGENT_RELEASES_KEY = ["agent-releases"] as const;
export const AGENT_TRUST_KEY = ["agent-trust"] as const;

/** Every published release, newest first. Platform admins only: pass their flag as `enabled`. */
export function useAgentReleasesQuery(enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: AGENT_RELEASES_KEY,
    queryFn: () => agentReleases.list(),
    enabled: live && enabled,
    staleTime: 30_000,
  });
}

/** The key updates published so far, in seq order. Platform admins only. */
export function useAgentTrustQuery(enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: AGENT_TRUST_KEY,
    queryFn: () => agentReleases.trust(),
    enabled: live && enabled,
    staleTime: 60_000,
  });
}

function useReleaseMutation<V>(fn: (v: V) => Promise<AgentRelease>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (release) => {
      qc.setQueryData<{ items: AgentRelease[] }>(AGENT_RELEASES_KEY, (old) =>
        old ? { items: old.items.map((r) => (r.id === release.id ? release : r)) } : old,
      );
      void qc.invalidateQueries({ queryKey: AGENT_RELEASES_KEY });
    },
  });
}

export function useWithdrawAgentRelease() {
  return useReleaseMutation((id: string) => agentReleases.withdraw(id));
}

export function useSetAgentReleaseUrgent() {
  return useReleaseMutation((v: { id: string; urgent: boolean }) => agentReleases.setUrgent(v.id, v.urgent));
}

// ── Agent log bundles (platform admins) ──────────────────────────────────────

export const AGENT_LOGS_KEY = ["agent-logs"] as const;

/**
 * The newest log bundles, for one org (`orgId`) or every org (null). Platform admins only: pass their flag as
 * `enabled`. Read again every minute, and every 10 s while `waiting` says, of the list read last, that a PC asked for
 * its logs has not sent them yet.
 */
export function useAgentLogsQuery(
  orgId: string | null,
  enabled: boolean,
  waiting: (items: AgentLogBundle[]) => boolean = () => false,
) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: [...AGENT_LOGS_KEY, orgId ?? "all"],
    queryFn: () => agentLogs.list(orgId),
    enabled: live && enabled,
    staleTime: 10_000,
    refetchInterval: (q) => (waiting(q.state.data?.items ?? []) ? 10_000 : 60_000),
  });
}

/** A bundle's diagnostics. A bundle never changes, so it is read once; a 404 or a 422 is final, so not retried. */
export function useAgentLogDiagnosticsQuery(id: string | null, enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["agent-log-diagnostics", id],
    queryFn: () => agentLogs.diagnostics(id as string),
    enabled: live && enabled && id !== null,
    staleTime: Infinity,
    retry: false,
  });
}

/** An agent's online history (org admin, in the agent's org). */
export function useAgentPresenceQuery(agentId: string, enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["ingest-agent-presence", agentId],
    queryFn: () => agentLogs.presence(agentId),
    enabled: live && enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

/** Asks a PC for its logs; the list is read again at once. */
export function useRequestAgentLogs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => agentLogs.request(agentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_LOGS_KEY });
    },
  });
}
